import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Animated,
  Dimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@/constants/colors';
import { GroupMember } from '@/services/groups/groupService';

const { height: SCREEN_HEIGHT } = Dimensions.get('window');
const MAX_HEIGHT = 280; // Maximum height for the autocomplete list

interface MentionAutocompleteProps {
  visible: boolean;
  suggestions: GroupMember[];
  onSelect: (member: GroupMember) => void;
  currentUserId?: string;
}

export const MentionAutocomplete: React.FC<MentionAutocompleteProps> = ({
  visible,
  suggestions,
  onSelect,
  currentUserId,
}) => {
  const [animatedHeight] = React.useState(new Animated.Value(0));

  React.useEffect(() => {
    if (visible && suggestions.length > 0) {
      console.log(
        `✨ [MentionAutocomplete] Showing autocomplete with ${suggestions.length} suggestions`
      );
      Animated.spring(animatedHeight, {
        toValue: 1,
        useNativeDriver: false,
        tension: 80,
        friction: 10,
      }).start();
    } else {
      Animated.timing(animatedHeight, {
        toValue: 0,
        duration: 200,
        useNativeDriver: false,
      }).start();
    }
  }, [visible, suggestions.length]);

  if (!visible || suggestions.length === 0) {
    return null;
  }

  // Calculate dynamic height based on number of items
  const itemHeight = 60;
  const headerHeight = 40;
  const listHeight = Math.min(suggestions.length * itemHeight, MAX_HEIGHT - headerHeight);
  const totalHeight = listHeight + headerHeight;

  const animatedStyle = {
    maxHeight: animatedHeight.interpolate({
      inputRange: [0, 1],
      outputRange: [0, totalHeight],
    }),
    opacity: animatedHeight,
  };

  const renderMemberItem = ({ item, index }: { item: GroupMember; index: number }) => {
    const isCurrentUser = item.user_id === currentUserId;

    return (
      <TouchableOpacity
        style={[styles.memberItem, index === suggestions.length - 1 && styles.lastMemberItem]}
        onPress={() => onSelect(item)}
        activeOpacity={0.6}
      >
        {/* Avatar */}
        <View style={styles.avatarContainer}>
          <View style={styles.avatar}>
            <Text style={styles.avatarText}>{item.user.name.charAt(0).toUpperCase()}</Text>
          </View>
        </View>

        {/* Member Info */}
        <View style={styles.memberInfo}>
          <View style={styles.nameRow}>
            <Text style={styles.memberName} numberOfLines={1}>
              {item.user.name}
            </Text>
            {isCurrentUser && (
              <View style={styles.youBadge}>
                <Text style={styles.youBadgeText}>You</Text>
              </View>
            )}
          </View>

          {item.role !== 'member' && <Text style={styles.roleText}>{item.role}</Text>}
        </View>

        {/* @ Icon */}
        <View style={styles.atIcon}>
          <Ionicons name="at-outline" size={18} color={Colors.accent.primary} />
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <Animated.View style={[styles.container, animatedStyle]}>
      {/* Header */}
      <View style={styles.header}>
        <Ionicons name="at" size={16} color={Colors.accent.primary} />
        <Text style={styles.headerText}>
          {suggestions.length === 1 ? '1 person' : `${suggestions.length} people`}
        </Text>
      </View>

      {/* Members List */}
      <FlatList
        data={suggestions}
        keyExtractor={item => item.user_id}
        renderItem={renderMemberItem}
        style={styles.list}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        bounces={false}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        initialNumToRender={5}
        windowSize={3}
      />
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: Colors.background.secondary,
    borderTopWidth: 1,
    borderTopColor: Colors.border.subtle,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
    zIndex: 1000,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: `${Colors.accent.primary}10`,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
    gap: 8,
  },
  headerText: {
    fontSize: 13,
    fontWeight: '600',
    color: Colors.accent.primary,
  },
  list: {
    flex: 1,
  },
  memberItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: Colors.background.secondary,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Colors.border.subtle,
    gap: 12,
  },
  lastMemberItem: {
    borderBottomWidth: 0,
  },
  avatarContainer: {
    width: 40,
    height: 40,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.accent.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarText: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  memberInfo: {
    flex: 1,
    gap: 4,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  memberName: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
    flex: 1,
  },
  youBadge: {
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 8,
  },
  youBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  roleText: {
    fontSize: 12,
    fontWeight: '500',
    color: Colors.text.tertiary,
    textTransform: 'capitalize',
  },
  atIcon: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: `${Colors.accent.primary}15`,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
